{ MidiEvent } from @danielx/lib

type MidiSong = import('./song.civet').default

// Practice metadata travels IN the MIDI: a text meta event at tick 0 whose
// payload is "PRACTICE:" + JSON. It rides along with the rest of the events
// through save/load, .mid export, and undo, and text events are never drawn
// (showText is off) or sounded.
PREFIX := "PRACTICE:"

export type PracticePart =
  // display name: "RH", "LH", "Melody", ...
  name: string
  // Whole MIDI channels the player performs (composer-native)...
  channels?: number[]
  // ...or individual notes by id — "tick:channel:pitch" with a ":k" suffix
  // for the k-th duplicate — which is what a Synthesia hand split converts to
  notes?: string[]

export type PracticeMetadata =
  version: number
  // base64 MD5 of the originally imported .mid: the identity link to
  // Synthesia-style sidecar metadata, recorded before any edit re-keys the
  // file
  sourceMd5?: string
  title?: string
  // Synthesia's 0-100 scale
  difficulty?: number
  parts: PracticePart[]
  // notes neither played back nor judged (Synthesia's X)
  discard?: string[]
  // Sections by measure and per-note fingering are authored in the practice
  // setup editor (future work) or imported; practice mode treats present
  // metadata as complete.
  sections?: {name?: string, startMeasure: number, endMeasure: number}[]
  // note id -> fingering: "L1".."L5", "R1".."R5", substitutions chain as
  // "L1sL2"
  fingering?: Record<string, string>

isPracticeEvent := (e: MidiEvent) ->
  e.type is "text" and e.text.startsWith PREFIX

export readPracticeMetadata := (song: MidiSong): PracticeMetadata? ->
  e := song.events.find isPracticeEvent
  return unless e
  try
    JSON.parse e.text.slice PREFIX.length

export writePracticeMetadata := (song: MidiSong, meta: PracticeMetadata): void ->
  // replace any existing practice event
  remaining := song.events.filter (e) -> !isPracticeEvent e
  song.events.length = 0
  song.events.push ...remaining

  // The event reader decodes the payload as ASCII, so escape anything beyond
  // it into JSON \u escapes (only strings can hold non-ASCII in JSON)
  json := (PREFIX + JSON.stringify meta).replace /[\u007f-\uffff]/g, (ch) ->
    "\\u" + ch.charCodeAt(0).toString(16).padStart(4, "0")

  bytes := json.split("").map (ch) -> ch.charCodeAt 0
  length := varint bytes.length
  // meta text event: 0xff 0x01 <varint length> <ascii payload>
  data := new Uint8Array [0xff, 0x01, ...length, ...bytes]

  song.events.push new MidiEvent 0, data
  song.sortEvents()
  return

export channelOfNoteId := (id: string): number ->
  parseInt id.split(":")[1], 10

// Display form of a stored fingering: "L1" -> 1 on the left hand,
// "R3sR4" (substitution) -> "3-4" on the right
export fingeringDisplay := (fingering: string): {text: string, hand: string} ->
  hand: fingering[0]
  text: fingering.split("s").map((f) -> f.slice 1).join "-"

// The union of every part's channels — from whole-channel parts and from the
// channels of note-level parts. Phase 1 mutes and reroutes at this
// granularity; judging uses the parts' exact notes.
export practiceChannels := (meta: PracticeMetadata): number[] ->
  channels := new Set<number>
  meta.parts.forEach (part) ->
    part.channels?.forEach (c) -> channels.add c
    part.notes?.forEach (id) -> channels.add channelOfNoteId id
  [...channels]

varint := (n: number) ->
  bytes := [n & 0x7f]
  n >>= 7
  while n > 0
    bytes.unshift (n & 0x7f) | 0x80
    n >>= 7
  bytes
